import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Skeleton,
  Stack,
  Grid,
} from '@mui/material';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import { SpecBreakdownReport } from '../../../types/reporting';

interface SpecBreakdownChartProps {
  data?: SpecBreakdownReport;
  loading?: boolean;
}

const PALETTE = [
  '#2196F3',
  '#4CAF50',
  '#FF9800',
  '#9C27B0',
  '#F44336',
  '#00BCD4',
  '#795548',
  '#607D8B',
];

const SpecBreakdownChart: React.FC<SpecBreakdownChartProps> = ({ data, loading = false }) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Skeleton variant="text" width="40%" height={28} />
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Skeleton variant="circular" width={150} height={150} />
            <Skeleton variant="circular" width={150} height={150} />
            <Skeleton variant="circular" width={150} height={150} />
          </Stack>
        </CardContent>
      </Card>
    );
  }

  const categories = data?.categories ?? [];

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Kitchen Spec Breakdown
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Distribution of spec-sheet selections across in-period jobs.
        </Typography>
        {categories.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 240,
            }}
          >
            <Typography color="text.secondary">
              No spec-sheet submissions in this period.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {categories.map((category) => {
              const chartData = category.items.map((item, i) => ({
                name: item.value,
                value: item.jobCount,
                percentage: item.percentage,
                color: PALETTE[i % PALETTE.length],
              }));
              return (
                <Grid item xs={12} md={4} key={category.categoryCode}>
                  <Stack alignItems="center" spacing={1}>
                    <Typography variant="subtitle2" align="center">
                      {category.categoryLabel}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {category.totalJobs} job{category.totalJobs === 1 ? '' : 's'}
                    </Typography>
                    <Box sx={{ width: '100%', height: 220 }}>
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie
                            data={chartData}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={80}
                            paddingAngle={2}
                            dataKey="value"
                          >
                            {chartData.map((entry, i) => (
                              <Cell key={i} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{
                              backgroundColor: theme.palette.background.paper,
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: 4,
                            }}
                            formatter={(value: number, _name: string, entry: any) => [
                              `${value} (${entry.payload.percentage}%)`,
                              entry.payload.name,
                            ]}
                          />
                          <Legend
                            layout="horizontal"
                            align="center"
                            verticalAlign="bottom"
                            wrapperStyle={{ fontSize: 11 }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </Stack>
                </Grid>
              );
            })}
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default SpecBreakdownChart;
